import React, { useState, useRef } from 'react';
import { Coffee } from 'lucide-react';
import { COFFEE_DATA } from '../../data/config';
import WidgetContainer from '../WidgetContainer';

const CoffeeTracker = ({ delay = 0 }) => {
  const [clickCount, setClickCount] = useState(0);
  const [steamHoverCount, setSteamHoverCount] = useState(0);
  const [showAchievement, setShowAchievement] = useState(false);
  const [intenseSteam, setIntenseSteam] = useState(false);
  const [shake, setShake] = useState(false);
  
  const todayPercentage = (COFFEE_DATA.today / COFFEE_DATA.todayGoal) * 100;
  const weekPercentage = (COFFEE_DATA.week / COFFEE_DATA.weekGoal) * 100;

  const handleCoffeeClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 10) {
      setShowAchievement(true);
      setTimeout(() => setShowAchievement(false), 3000);
    }
  };

  const handleSteamHover = () => {
    const newCount = steamHoverCount + 1;
    setSteamHoverCount(newCount);
    
    if (newCount >= 5) {
      setIntenseSteam(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <WidgetContainer size="1x2" delay={delay} withSteam>
      {/* Achievement Popup */}
      {showAchievement && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'linear-gradient(135deg, rgba(255, 232, 200, 0.95), rgba(237, 197, 143, 0.95))',
          padding: '1.5rem',
          borderRadius: '1rem',
          zIndex: 100,
          boxShadow: '0 10px 40px rgba(255, 232, 200, 0.5)',
          animation: 'achievementPop 0.5s ease-out',
          textAlign: 'center',
          border: '2px solid rgba(255, 232, 200, 1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>☕</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#0a0603', marginBottom: '0.25rem' }}>
            Coffee Addict Badge
          </div>
          <div style={{ fontSize: '0.9rem', color: '#0a0603' }}>
            Unlocked!
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4" style={{ position: 'relative' }}>
        <h3 className="text-lg font-semibold text-cream-100">Coffee Tracker</h3>
        <div 
          style={{ position: 'relative', cursor: 'pointer' }}
          onClick={handleCoffeeClick}
          onMouseEnter={handleSteamHover}
        >
          <Coffee 
            className="w-5 h-5 text-cream-200" 
            style={{
              filter: 'drop-shadow(0 0 8px rgba(255, 232, 200, 0.6))',
              zIndex: 2,
              position: 'relative',
              animation: shake ? 'shake 0.5s ease-in-out' : 'none',
              transition: 'transform 0.1s'
            }}
          />
          {/* Coffee steam lines rising from cup */}
          <div 
            style={{
              position: 'absolute',
              bottom: '60%',
              left: '38%',
              width: intenseSteam ? '3px' : '2px',
              height: intenseSteam ? '20px' : '12px',
              background: 'linear-gradient(to top, rgba(255, 232, 200, 0.5), transparent)',
              borderRadius: '2px',
              filter: intenseSteam ? 'blur(2px)' : 'blur(1px)',
              animation: intenseSteam ? 'steam 1.5s ease-in-out infinite' : 'steam 2.5s ease-in-out infinite',
            }}
          />
          <div 
            style={{
              position: 'absolute',
              bottom: '60%',
              left: '48%',
              width: intenseSteam ? '3px' : '2px',
              height: intenseSteam ? '25px' : '15px',
              background: 'linear-gradient(to top, rgba(237, 197, 143, 0.6), transparent)',
              borderRadius: '2px',
              filter: intenseSteam ? 'blur(2.5px)' : 'blur(1.5px)',
              animation: intenseSteam ? 'steam 1.8s ease-in-out infinite 0.4s' : 'steam 3s ease-in-out infinite 0.4s',
            }}
          />
          <div 
            style={{
              position: 'absolute',
              bottom: '60%',
              left: '58%',
              width: intenseSteam ? '3px' : '2px',
              height: intenseSteam ? '22px' : '13px',
              background: 'linear-gradient(to top, rgba(255, 218, 168, 0.55), transparent)',
              borderRadius: '2px',
              filter: intenseSteam ? 'blur(2.2px)' : 'blur(1.2px)',
              animation: intenseSteam ? 'steam 1.6s ease-in-out infinite 0.8s' : 'steam 2.8s ease-in-out infinite 0.8s',
            }}
          />
        </div>
      </div>

      {/* Today's Coffee */}
      <div className="mb-6">
        <div className="flex justify-between items-end mb-2">
          <span className="text-sm text-coffee-200">Today</span>
          <div className="text-right">
            <span className="text-2xl font-bold text-cream-100">{COFFEE_DATA.today}</span>
            <span className="text-sm text-coffee-300">/{COFFEE_DATA.todayGoal}</span>
          </div>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${Math.min(todayPercentage, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* This Week */}
      <div className="mb-6">
        <div className="flex justify-between items-end mb-2">
          <span className="text-sm text-coffee-200">This Week</span>
          <div className="text-right">
            <span className="text-xl font-bold text-cream-100">{COFFEE_DATA.week}</span>
            <span className="text-sm text-coffee-300">/{COFFEE_DATA.weekGoal}</span>
          </div>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${Math.min(weekPercentage, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-coffee-700/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-cream-100">{COFFEE_DATA.total.toLocaleString()}</div>
          <div className="text-xs text-coffee-200">All Time</div>
        </div>
        <div className="bg-coffee-700/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-cream-100">{COFFEE_DATA.avgPerDay}</div>
          <div className="text-xs text-coffee-200">Avg / Day</div>
        </div>
      </div>

      <style>{`
        @keyframes achievementPop {
          0% {
            transform: translate(-50%, -50%) scale(0) rotate(-180deg);
            opacity: 0;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2) rotate(10deg);
          }
          100% {
            transform: translate(-50%, -50%) scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        @keyframes shake {
          0%, 100% { transform: rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: rotate(-5deg); }
          20%, 40%, 60%, 80% { transform: rotate(5deg); }
        }
      `}</style>
    </WidgetContainer>
  );
};

export default CoffeeTracker;
